import { useCallback, useEffect, useState } from "react"

import {
  isIntegrationScanner,
  type ScannerIntegrationConfig,
} from "./scannerIntegrations.config"

type Params = {
  showScannerPatients: boolean
  scannerSelectData: { key: string }[]
  setShowScannerPatients: (show: boolean) => void
}

/** Suppress-after-not-connected flow and single-option Select edge case. Device scanners unchanged. */
export function useIntegrationScannerOptions({
  showScannerPatients,
  scannerSelectData,
  setShowScannerPatients,
}: Params) {
  /** Keys hidden from dropdown for this dialog session (after not-connected OK). */
  const [suppressedScanners, setSuppressedScanners] = useState(() => new Set<string>())
  const [notConnectedDialogScanner, setNotConnectedDialogScanner] = useState<string | null>(
    null,
  )

  useEffect(() => {
    if (!showScannerPatients) {
      setSuppressedScanners(new Set())
      setNotConnectedDialogScanner(null)
    }
  }, [showScannerPatients]) // reset when dialog closes

  const suppressScanner = useCallback((key: string) => {
    setSuppressedScanners((prev) => new Set(prev).add(key))
  }, [])

  const closeNotConnectedDialog = useCallback(() => {
    setNotConnectedDialogScanner(null)
  }, [])

  const isIntegrationListAllowed = useCallback(
    (key: string) => isIntegrationScanner(key) && !suppressedScanners.has(key),
    [suppressedScanners],
  )

  const shouldIncludeIntegration = useCallback(
    (config: ScannerIntegrationConfig) => !suppressedScanners.has(config.key),
    [suppressedScanners],
  )

  useEffect(() => {
    if (!showScannerPatients) {
      return
    }
    // Last option was integration and user dismissed not-connected → empty list.
    if (suppressedScanners.size > 0 && scannerSelectData.length === 0) {
      setShowScannerPatients(false)
    }
  }, [
    showScannerPatients,
    suppressedScanners,
    scannerSelectData.length,
    setShowScannerPatients,
  ])

  return {
    suppressedScanners,
    suppressScanner,
    notConnectedDialogScanner,
    setNotConnectedDialogScanner,
    closeNotConnectedDialog,
    isIntegrationListAllowed,
    shouldIncludeIntegration,
  }
}
